// The browser and the PDF export hand `buildSlicerValues` the roster, the date-range
// labels and the day labels through this wrapper. It must stay free of antd, react-dom
// and TipTap: the PDF Lambda bundles it through the title and text cores.

import { GAMEDAY_TAGS, WEEKDAY_TAGS } from "../AddWidgetModal/constants";
import { controllerAthlete, type ControllerOption } from "../Processing/controllerBindings";
import type {
  ControllerKind,
  ProcessedWidgetData,
  SlicerResponseData,
  SubjectEntity,
} from "../Processing/types";
import type { DynamicControllerBuilderMisc } from "../Processing/types/builderMisc";
import { resolveDateRange } from "./resolveDateRange";
import type { SlicerValues } from "./resolveTemplate";
import { buildSlicerValues } from "./slicerVariables.mjs";
import { resolveSubjectNames } from "./subjectNames";

const DAY_LABELS: Record<string, string> = {};
for (const tag of [...WEEKDAY_TAGS, ...GAMEDAY_TAGS]) DAY_LABELS[tag.value] = tag.label;

/**
 * The slicer value map `resolveTemplate` reads, for every slicer on the board.
 *
 * `controllers` come from `listControllers` in widget order. `getSubjectEntities`
 * resolves the subject ids to names; an id it does not know prints "Unknown".
 * `engineByUuid` is the backend's answer per slicer, keyed by the controller uuid
 * (see `slicerEngineByUuid`); a slicer with one reads its dates and roots from it.
 */
export function slicerValuesFor(
  controllers: ControllerOption[],
  getSubjectEntities: (subjectType: string) => SubjectEntity[],
  today: Date = new Date(),
  engineByUuid?: Record<string, SlicerResponseData>,
): SlicerValues {
  const slicers = controllers.map((controller) => ({
    key: controller.key,
    label: controller.label,
    controls: controller.controls,
    misc: controller.spec.slicer_builder_misc ?? {},
    engine: engineByUuid?.[controller.uuid],
  }));
  const subjectNames = (subjectIds: string[], drillIds: string[]): string[] =>
    resolveSubjectNames(subjectIds, drillIds, getSubjectEntities);
  const firstAthleteName = (
    controls: readonly ControllerKind[] | undefined,
    misc: DynamicControllerBuilderMisc | undefined,
  ): string | null => {
    const athleteId = controllerAthlete(controls, misc);
    return athleteId ? subjectNames([athleteId], [])[0] ?? null : null;
  };
  return buildSlicerValues(slicers, {
    today,
    dateRangeLabel: resolveDateRange,
    subjectNames,
    firstAthleteName,
    dayLabel: (tag: string): string | undefined => DAY_LABELS[tag],
  }) as SlicerValues;
}

/**
 * The engine's answer per slicer, keyed by the controller uuid, from the processed data.
 *
 * A slicer whose data has not arrived yet, or whose edit reset it, is absent, so its
 * tokens read the client values until the next dashboard data lands.
 */
export function slicerEngineByUuid(
  controllers: ControllerOption[],
  processedData: Record<string, ProcessedWidgetData>,
): Record<string, SlicerResponseData> {
  const out: Record<string, SlicerResponseData> = {};
  for (const controller of controllers) {
    const processed = processedData[controller.id];
    if (processed?.type === "slicer" && processed.engine) out[controller.uuid] = processed.engine;
  }
  return out;
}
